import { useState, useEffect } from 'react';
import { pageService } from '@/services/page.service';
import type { Page } from '@/types/page';

export interface NavigationItem {
  name: string;
  href: string;
  slug: string;
  type: string;
  order?: number;
}

/**
 * Hook to get all published pages for navigation menu
 * Returns pages in navigation format with proper ordering
 */
export function usePublishedPages() {
  const [pages, setPages] = useState<NavigationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchPublishedPages() {
      // Wait for tenant config to be loaded in localStorage before making API call
      // This ensures the x-tenant-id header will be sent with the request
      const tenantConfig = localStorage.getItem('inmocms_tenant_config');

      if (!tenantConfig) {
        // Tenant config not loaded yet, wait a bit and retry
        const retryTimeout = setTimeout(() => {
          fetchPublishedPages();
        }, 100);

        return () => clearTimeout(retryTimeout);
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch all published pages
        const result = await pageService.getPages({
          status: 'PUBLISHED',
          includeSections: false, // We only need basic page info for navigation
          limit: 50 // Reasonable limit for navigation items
        });

        // Transform pages to navigation format
        const navItems: NavigationItem[] = result.pages.map((page: Page) => ({
          name: page.title,
          href: `/${page.slug}`,
          slug: page.slug,
          type: page.type,
          order: page.order || 0
        }));

        // Sort by order (if available) or alphabetically
        const sortedItems = navItems.sort((a, b) => {
          if (a.order !== undefined && b.order !== undefined) {
            return a.order - b.order;
          }
          return a.name.localeCompare(b.name);
        });

        setPages(sortedItems);
      } catch (err) {
        console.error('Error fetching published pages:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch pages');
        setPages([]);
      } finally {
        setLoading(false);
      }
    }

    fetchPublishedPages();
  }, []);

  return { pages, loading, error };
}
